import { useQuery } from "@tanstack/react-query";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface DotStatus {
  depositRequired: boolean;
  depositPending: boolean;
  docsRequired: boolean;
  docsPending: boolean;
  hasAnyRequirement: boolean;
  allClear: boolean;
}

interface InvoiceStatusDotProps {
  invoiceId: string;
  initialStatus?: Partial<DotStatus>;
  size?: "sm" | "md";
}

export function InvoiceStatusDot({
  invoiceId,
  initialStatus,
  size = "sm",
}: InvoiceStatusDotProps) {
  const { data } = useQuery<DotStatus>({
    queryKey: ["/api/invoices", invoiceId, "dot-status"],
    initialData: initialStatus
      ? ({
          depositRequired: false,
          depositPending: false,
          docsRequired: false,
          docsPending: false,
          hasAnyRequirement: false,
          allClear: true,
          ...initialStatus,
        } as DotStatus)
      : undefined,
    staleTime: 15_000,
  });

  if (!data || !data.hasAnyRequirement) return null;

  const dim = size === "md" ? "h-3 w-3" : "h-2.5 w-2.5";
  const isYellow = data.depositPending || data.docsPending;
  const colorCls = isYellow
    ? "bg-yellow-400 ring-yellow-200 dark:ring-yellow-700"
    : "bg-green-500 ring-green-200 dark:ring-green-700";

  const tooltipLines: string[] = [];
  if (data.depositRequired) {
    tooltipLines.push(
      data.depositPending ? "Deposit pending" : "Deposit received",
    );
  }
  if (data.docsRequired) {
    tooltipLines.push(
      data.docsPending ? "Documents pending" : "Documents received",
    );
  }

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            className={`inline-block rounded-full ring-2 ${dim} ${colorCls}`}
            data-testid={`dot-invoice-status-${invoiceId}`}
            aria-label={tooltipLines.join(", ") || "All clear"}
          />
        </TooltipTrigger>
        <TooltipContent side="top" className="text-xs">
          <div className="space-y-0.5">
            {tooltipLines.map((line) => (
              <div key={line}>{line}</div>
            ))}
            {!isYellow && tooltipLines.length === 0 && <div>All clear</div>}
          </div>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
}
